import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/ui/Header";
import HeroSection from "./components/HeroSection";
import NavigationTabs from "./components/NavigationTabs";
import PropertyGrid from "./components/PropertyGrid";
import FloatingContactButton from "./components/FloatingContactButton";
import Icon from "../../components/AppIcon";
import { PRICING_BY_SLUG, getPriceForDate } from "../../data/pricing";
import { APARTMENTS, ROOMS } from "../../data/properties";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/ui/Footer";

const PropertyHomepage = () => {
  const getTabFromPath = (pathname) => {
    if (pathname.startsWith("/sobe")) return "rooms";
    if (pathname.startsWith("/apartmani")) return "apartments";
    return "apartments";
  };
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    getTabFromPath(location.pathname)
  );
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  const [loading, setLoading] = useState(false);

  const listRef = useRef(null);
  const scrollToList = () => {
    if (!listRef.current) return;

    const isMobile = window.innerWidth < 768;
    const headerOffset = isMobile ? 220 : 155;

    const y =
      listRef.current.getBoundingClientRect().top +
      window.pageYOffset -
      headerOffset;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const p = location.pathname;
    const shouldScroll = p === "/sobe" || p === "/apartmani";
    if (!shouldScroll) return;

    let rafId;
    let tries = 0;

    const tryScroll = () => {
      if (!listRef.current) {
        tries += 1;
        if (tries < 30) rafId = requestAnimationFrame(tryScroll);
        return;
      }
      scrollToList();
    };

    rafId = requestAnimationFrame(tryScroll);
    return () => rafId && cancelAnimationFrame(rafId);
  }, [location.pathname, activeTab]);

  const getTzDateParts = (timeZone = "Europe/Zagreb") => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    }).formatToParts(new Date());
    const get = (t) => Number(parts.find((p) => p.type === t)?.value);
    return { y: get("year"), m: get("month"), d: get("day") };
  };
  const { y, m, d } = getTzDateParts("Europe/Zagreb");
  const today = new Date(Date.UTC(y, m - 1, d));

  const getCurrentProperties = () => {
    const list = activeTab === "apartments" ? APARTMENTS ?? [] : ROOMS ?? [];

    return list.map((property) => {
      const seasonalPrices = PRICING_BY_SLUG[property.slug];
      const todayPrice = getPriceForDate(seasonalPrices, today);

      return {
        ...property,
        image: property.image ?? property.images?.[0],
        price: todayPrice,
      };
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Guest House Sonja Krk | Apartments & Rooms Near Center</title>
        <meta
          name="description"
          content="Guest House Sonja Krk offers modern apartments and rooms near the center of Krk, Croatia. Free WiFi, free parking, air conditioning, terrace and BBQ. Book direct."
        />
        <link rel="canonical" href="https://www.guesthousesonjakrk.com/" />
        <meta name="robots" content="index,follow" />
      </Helmet>
      <Header />
      <HeroSection />
      <NavigationTabs
        activeTab={activeTab}
        onTabChange={(tabId) => {
          setActiveTab(tabId);
          if (location.pathname === "/") {
            // samo na Home skrolaj na listu kad user klikne tab
            requestAnimationFrame(scrollToList);
          }
        }}
      />

      <main className="container mx-auto px-6 lg:px-8 py-12">
        <div ref={listRef} className="mb-8">
          <h2 className="font-heading text-3xl font-bold text-foreground mb-4">
            {activeTab === "apartments" ? "Our Apartments" : "Our Rooms"}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl">
            {activeTab === "apartments"
              ? "Explore our carefully curated apartments, designed to offer a warm atmosphere, modern comfort, and everything you need for a perfect stay."
              : "Discover our comfortable and thoughtfully designed rooms, offering privacy, modern amenities, and a welcoming atmosphere."}
          </p>
        </div>

        {/* Property Grid */}
        <PropertyGrid
          properties={getCurrentProperties()}
          type={activeTab === "apartments" ? "apartment" : "room"}
          loading={loading}
        />

        {/* Why Choose Section */}
        <div className="mt-16 bg-card rounded-xl p-8 shadow-subtle">
          <div className="text-center max-w-3xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
              Why Choose Guest House Sonja?
            </h3>
            <p className="text-muted-foreground mb-6">
              Guest House Sonja Krk offers comfortable apartments and rooms in a
              quiet location near the center of Krk, Croatia. Our guest house is
              a perfect choice for couples, families and solo travelers looking
              for quality accommodation with a homely atmosphere. Guests can
              enjoy free WiFi, private parking, air conditioning, a terrace and
              BBQ. The historic old town of Krk, beaches, restaurants and local
              shops are all within easy walking distance.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="MapPin" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold">Good Location</h4>
                <p className="text-sm text-muted-foreground">
                  Stay close to everything that makes Krk special
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Star" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold">Premium Quality</h4>
                <p className="text-sm text-muted-foreground">
                  High-quality furnishing and modern amenities
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="Clock" size={24} className="text-primary" />
                </div>
                <h4 className="font-semibold">Always Available</h4>
                <p className="text-sm text-muted-foreground">
                  We’re here to help throughout your stay
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingContactButton />
    </div>
  );
};

export default PropertyHomepage;
